import React, { useCallback, useEffect, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Layout,
	Button,
	Card,
	Table,
	Select,
	Switch,
	DatePicker,
	message,
	Popconfirm,
	Space,
	Typography,
	ConfigProvider,
} from "antd";
import AttendanceNavigation from "../attendance/AttendanceNavigation";
import OfficeTeamNavigation from "./OfficeTeamNavigation";
import dayjs from "dayjs";
import locale from "antd/locale/ko_KR";
import "dayjs/locale/ko";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;

const usesAttendanceNavigation = () => {
	try {
		const groups = JSON.parse(localStorage.getItem("user_groups") || "[]");
		return Array.isArray(groups) && groups.includes("office_leave");
	} catch {
		return false;
	}
};

const AssignEmployeeTeamMembers = () => {
	const isMobile = useIsMobile();
	const [teams, setTeams] = useState([]);
	const [teamId, setTeamId] = useState(undefined);
	const [members, setMembers] = useState([]);
	const [clergyStaff, setClergyStaff] = useState([]);
	const [loadingTeams, setLoadingTeams] = useState(false);
	const [loadingMembers, setLoadingMembers] = useState(false);
	const [addUserId, setAddUserId] = useState(undefined);
	const [addLeader, setAddLeader] = useState(false);
	const [addStart, setAddStart] = useState(null);
	const [addEnd, setAddEnd] = useState(null);
	const [updatingMemberId, setUpdatingMemberId] = useState(null);

	const loadTeams = useCallback(async () => {
		try {
			setLoadingTeams(true);
			const res = await useAxios.get("/users/employee-teams/");
			if (res.status === 200) {
				setTeams(res.data);
			}
		} catch {
			message.error("팀 목록을 불러오지 못했습니다.");
		} finally {
			setLoadingTeams(false);
		}
	}, []);

	const loadClergy = useCallback(async () => {
		try {
			const res = await useAxios.get("/users/clergy-staff/");
			if (res.status === 200) {
				setClergyStaff(res.data);
			}
		} catch {
			message.error(
				"교역자·교직원 목록을 불러오지 못했습니다.",
			);
		}
	}, []);

	const loadMembers = useCallback(async (id) => {
		if (!id) {
			setMembers([]);
			return;
		}
		try {
			setLoadingMembers(true);
			const res = await useAxios.get(
				`/users/employee-teams/${id}/members/`,
			);
			if (res.status === 200) {
				setMembers(res.data);
			}
		} catch {
			message.error("팀 멤버를 불러오지 못했습니다.");
		} finally {
			setLoadingMembers(false);
		}
	}, []);

	useEffect(() => {
		loadTeams();
		loadClergy();
	}, [loadTeams, loadClergy]);

	useEffect(() => {
		loadMembers(teamId);
	}, [teamId, loadMembers]);

	const memberUserIds = new Set(members.map((m) => m.user_id));
	const addOptions = clergyStaff.filter((u) => !memberUserIds.has(u.id));

	const handleAddMember = async () => {
		if (!teamId) {
			message.warning("팀을 선택하세요.");
			return;
		}
		if (!addUserId) {
			message.warning("추가할 사람을 선택하세요.");
			return;
		}
		try {
			await useAxios.post(
				`/users/employee-teams/${teamId}/members/`,
				{
					user_id: addUserId,
					is_leader: addLeader,
					start_date: addStart
						? addStart.format("YYYY-MM-DD")
						: null,
					end_date: addEnd
						? addEnd.format("YYYY-MM-DD")
						: null,
				},
			);
			message.success("멤버가 추가되었습니다.");
			setAddUserId(undefined);
			setAddLeader(false);
			setAddStart(null);
			setAddEnd(null);
			loadMembers(teamId);
			loadTeams();
		} catch (e) {
			const d = e.response?.data;
			if (d?.user_id) {
				message.error(
					Array.isArray(d.user_id)
						? d.user_id[0]
						: String(d.user_id),
				);
			} else if (d && typeof d === "object") {
				const first = Object.values(d).flat()[0];
				message.error(
					typeof first === "string"
						? first
						: "멤버 추가에 실패했습니다.",
				);
			} else {
				message.error("멤버 추가에 실패했습니다.");
			}
		}
	};

	const updateMember = async (id, payload) => {
		try {
			setUpdatingMemberId(id);
			await useAxios.put(
				`/users/employee-team-members/${id}/`,
				payload,
			);
			loadMembers(teamId);
		} catch {
			message.error("저장에 실패했습니다.");
		} finally {
			setUpdatingMemberId(null);
		}
	};

	const handleDeleteMember = async (id) => {
		try {
			await useAxios.delete(
				`/users/employee-team-members/${id}/`,
			);
			message.success("멤버에서 제외되었습니다.");
			loadMembers(teamId);
			loadTeams();
		} catch {
			message.error("삭제에 실패했습니다.");
		}
	};

	const columns = [
		{ title: "이름", dataIndex: "user_name" },
		{
			title: "구분",
			dataIndex: "classification_name",
			render: (v) => v || "—",
		},
		{
			title: "직책(직무)",
			dataIndex: "job",
			render: (v) => v || "—",
		},
		{
			title: "리더",
			dataIndex: "is_leader",
			render: (val, record) => (
				<Switch
					checked={val}
					loading={updatingMemberId === record.id}
					onChange={(checked) =>
						updateMember(record.id, {
							is_leader: checked,
						})
					}
				/>
			),
		},
		{
			title: "시작일",
			dataIndex: "start_date",
			render: (val, record) => (
				<DatePicker
					inputReadOnly={isMobile}
					value={val ? dayjs(val) : null}
					onChange={(d) =>
						updateMember(record.id, {
							start_date: d
								? d.format("YYYY-MM-DD")
								: null,
						})
					}
					style={{ width: "100%" }}
					allowClear
				/>
			),
		},
		{
			title: "종료일",
			dataIndex: "end_date",
			render: (val, record) => (
				<DatePicker
					inputReadOnly={isMobile}
					value={val ? dayjs(val) : null}
					onChange={(d) =>
						updateMember(record.id, {
							end_date: d
								? d.format("YYYY-MM-DD")
								: null,
						})
					}
					style={{ width: "100%" }}
					allowClear
				/>
			),
		},
		{
			title: "",
			key: "del",
			width: 88,
			render: (_, record) => (
				<Popconfirm
					title="이 멤버를 팀에서 제외할까요?"
					okText="제외"
					cancelText="취소"
					onConfirm={() =>
						handleDeleteMember(record.id)
					}>
					<Button type="link" danger size="small">
						제외
					</Button>
				</Popconfirm>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="팀별 멤버 지정" />
			<Layout className="member-layout">
				{usesAttendanceNavigation() ? (
					<AttendanceNavigation />
				) : (
					<OfficeTeamNavigation />
				)}
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="page">
							<PageHeader
								title="팀별 멤버 지정"
							/>
							<Card className="surface-card">
								<Space
									direction="vertical"
									style={{
										width: "100%",
									}}
									size="large">
									<div>
										<Typography.Text strong>
											팀 선택
										</Typography.Text>
										<Select
											style={{
												width: "100%",
												maxWidth: 360,
												marginTop: 8,
												display: "block",
											}}
											placeholder="팀을 선택하세요"
											loading={loadingTeams}
											allowClear
											value={teamId}
											onChange={(v) =>
												setTeamId(v)
											}
											options={teams.map((t) => ({
												value: t.id,
												label: `${t.name} (멤버 ${t.member_count}명)`,
											}))}
										/>
									</div>

									{teamId ? (
										<>
											<div>
												<Typography.Text strong>
													멤버 추가 (교역자·교직원)
												</Typography.Text>
												<Space
													wrap
													style={{
														marginTop: 12,
													}}>
													<Select
														showSearch
														allowClear
														placeholder="이름 검색"
														style={{
															minWidth: isMobile ? 0 : 240,
															width: isMobile ? "100%" : undefined,
														}}
														value={addUserId}
														onChange={
															setAddUserId
														}
														optionFilterProp="label"
														options={addOptions.map(
															(u) => ({
																value: u.id,
																label: `${u.name} (${u.classification_name || "—"})`,
															}),
														)}
													/>
													<Space>
														<span>
															리더
														</span>
														<Switch
															checked={
																addLeader
															}
															onChange={
																setAddLeader
															}
														/>
													</Space>
													<DatePicker
														inputReadOnly={isMobile}
														placeholder="시작일"
														value={addStart}
														onChange={
															setAddStart
														}
													/>
													<DatePicker
														inputReadOnly={isMobile}
														placeholder="종료일"
														value={addEnd}
														onChange={
															setAddEnd
														}
													/>
													<Button
														type="primary"
														onClick={
															handleAddMember
														}>
														추가
													</Button>
												</Space>
											</div>
											<Table
												rowKey="id"
												columns={columns}
												dataSource={members}
												loading={
													loadingMembers
												}
												pagination={false}
											/>
										</>
									) : (
										<Typography.Text type="secondary">
											팀을 선택하면 멤버 목록과 추가
											폼이 표시됩니다.
										</Typography.Text>
									)}
								</Space>
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default AssignEmployeeTeamMembers;
